import _ from 'lodash'
import { createPromise, hasTemplateContainer, getStaticBundleOverrideUrl } from '@wix/thunderbolt-commons'
import type { IComponentSdksManager, IBuilderComponentSdkLoader } from '../types'
import type { IModelsAPI, IPlatformLogger } from '@wix/thunderbolt-symbols'
import type { BootstrapData, ComponentSdks, ComponentSdksLoader, CoreSdkLoaders } from '../../types'
import {
	BOOTSTRAP_DATA,
	COMPONENT_SDKS_MANAGER,
	LOAD_COMPONENT_SDKS_PROMISE,
	MODELS_API,
	PLATFORM_LOGGER,
	BUILDER_COMPONENT_SDK_LOADER,
	SERVICES_LOADER,
	SET_PROPS_MANAGER,
} from './moduleNames'
import type {
	ComponentSdkFactory,
	BuilderBobComponentSdk,
	componentSdkFactoryArgs,
} from '@wix/thunderbolt-platform-types'
import type { GetService } from '@wix/services-manager/types'
import type { IServices } from './servicesLoader'
import type { ISetPropsManager } from './setPropsManager'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const getSdkTypesToLoad = (modelsApi: IModelsAPI, builderSlotsEnabled?: boolean) => {
	const compIdToConnections = modelsApi.getCompIdConnections()
	const structureModel = modelsApi.getStructureModel()
	const slots = modelsApi.getSlots()
	const builderComponentIds = _.keys(_.pickBy(structureModel, (comp) => comp.builderType))
	const propsModel = modelsApi.getPropsModel()

	const sdkCompTypes = [
		'PageBackground',
		..._(structureModel)
			.omit(builderComponentIds)
			.transform(
				(sdkTypes, compStructure, compId) => {
					if (compIdToConnections[compId]) {
						sdkTypes[compStructure.componentType] = true
					}

					if (!builderSlotsEnabled) {
						_.forEach(slots[compId], (slotCompId) => {
							sdkTypes[_.get(structureModel, [slotCompId, 'componentType'])] = true
						})
					}
				},
				{} as Record<string, boolean>
			)
			.keys()
			.value(),
		...(builderSlotsEnabled
			? _(slots)
					.flatMap((slotMap) => Object.values(slotMap))
					.map((slotCompId: string) => structureModel[slotCompId])
					.filter((comp) => Boolean(comp && !comp.builderType))
					.map((comp) => comp.componentType)
					.filter(Boolean)
					.value()
			: []),
	]

	const templateContainer = builderComponentIds.find((compId) => hasTemplateContainer(propsModel[compId]))

	if (templateContainer) {
		sdkCompTypes.push('TemplateContainer')
	}

	return sdkCompTypes
}

const getBuilderComponentsSdks = async (
	builderComponents: Record<string, string> = {},
	builderComponentSdkLoader: IBuilderComponentSdkLoader,
	logger: IPlatformLogger
): Promise<Record<string, any>> => {
	return Object.fromEntries(
		await Promise.all(
			Object.entries(builderComponents).map(async ([componentType, sdkUrl]) =>
				logger.runAsyncAndReport(`loadBuilderComponentSdk-${componentType}-${sdkUrl}`, () =>
					builderComponentSdkLoader
						.loadSdk(getStaticBundleOverrideUrl(componentType, 'sdk', sdkUrl))
						.then((sdk) => [componentType, sdk])
						.catch((error: Error) => {
							console.error(`Failed to fetch sdk for componentId: ${componentType} from ${sdkUrl}`, error)
							return [componentType, null]
						})
				)
			)
		)
	)
}

const ComponentSdksManager = (
	loadComponentSdksPromise: Promise<ComponentSdksLoader>,
	modelsApi: IModelsAPI,
	logger: IPlatformLogger,
	bootstrapData: BootstrapData,
	builderComponentSdkLoader: IBuilderComponentSdkLoader,
	servicesLoader: IServices,
	{ createSetPropsForNCM }: ISetPropsManager
): IComponentSdksManager => {
	const componentsSdks: ComponentSdks = {}
	const builderComponentsSdks: ComponentSdks = {}
	const builderBOB$wCache: Record<string, ComponentSdkFactory> = {}
	const { platformEnvData } = bootstrapData
	const shouldUseNewBuilderSdkApi = isExperimentOpen(
		platformEnvData.site.experiments,
		'specs.thunderbolt.useNewBuilderSdkApi'
	)
	const sdkTypesToCompTypesMapper: ComponentSdksLoader['sdkTypeToComponentTypes'] = {}
	const { resolver: sdkResolver, promise: sdkPromise } = createPromise()
	const getService: GetService = (serviceName) => servicesLoader.getService(serviceName)

	const loadCoreComponentSdks = async (compTypes: Array<string>, coreSdksLoaders: CoreSdkLoaders) => {
		const compsPromises = [...compTypes, 'Document', 'RefComponent']
			.filter((type) => coreSdksLoaders[type])
			.map((type) =>
				coreSdksLoaders[type]()
					.then((sdkFactory) => ({ [type]: sdkFactory }))
					.catch((e) => {
						if (e.name !== 'NetworkError') {
							logger.captureError(new Error('could not load core component SDKs from thunderbolt'), {
								groupErrorsBy: 'values',
								tags: { method: 'loadCoreComponentSdks', error: `${e.name}: ${e.message}` },
								extra: { type },
							})
						}
						return {}
					})
			)
		const sdksArray = await Promise.all(compsPromises)
		return Object.assign({}, ...sdksArray)
	}

	return {
		async fetchComponentsSdks(coreSdksLoaders: CoreSdkLoaders) {
			const builderSlotsEnabled = isExperimentOpen(
				platformEnvData.site.experiments,
				'specs.thunderbolt.builderComponentSlotsInPlatform'
			)
			const compTypes = getSdkTypesToLoad(modelsApi, builderSlotsEnabled)
			logger.interactionStarted('loadComponentSdk')
			const { loadComponentSdks, sdkTypeToComponentTypes } = await loadComponentSdksPromise
			Object.assign(sdkTypesToCompTypesMapper, sdkTypeToComponentTypes || {})
			Object.assign(
				builderComponentsSdks,
				await getBuilderComponentsSdks(
					platformEnvData.builderComponentsCompTypeSdkUrls,
					builderComponentSdkLoader,
					logger
				)
			)
			if (!loadComponentSdks) {
				sdkResolver()
				return
			}
			const componentSdksPromise = loadComponentSdks(compTypes, logger).catch((e) => {
				if (e.name !== 'NetworkError') {
					logger.captureError(new Error('could not load component SDKs from loadComponentSdks function'), {
						groupErrorsBy: 'values',
						tags: {
							errorType: 'load-component-SDKs-failed',
							method: 'loadComponentSdks',
							error: `${e.name}: ${e.message}`,
						},
						extra: {
							compTypes,
							componentsRegistry: bootstrapData.platformEnvData.componentsRegistry,
							...(e.extraParams ? e.extraParams : {}),
						},
					})
				}
				return {}
			})
			const [coreSdks, sdks] = await Promise.all([
				loadCoreComponentSdks(compTypes, coreSdksLoaders),
				componentSdksPromise,
			]).catch(() => [])
			Object.assign(componentsSdks, sdks, coreSdks)
			sdkResolver()
			logger.interactionEnded('loadComponentSdk')
		},
		waitForSdksToLoad() {
			return sdkPromise
		},
		getBuilderBOBConnections(compType) {
			const builderComponentSdk = builderComponentsSdks[compType] as unknown as BuilderBobComponentSdk

			if (builderComponentSdk && builderComponentSdk.connections && builderComponentSdk.pageId) {
				return {
					connections: builderComponentSdk.connections,
					pageId: builderComponentSdk.pageId,
				}
			}
			return undefined
		},
		getBuilderBOB$w(compId, getSlot) {
			const compType = modelsApi.getCompType(compId) || ''

			if (builderBOB$wCache[compId]) {
				return builderBOB$wCache[compId]
			}

			const componentSdk = builderComponentsSdks[compType] as unknown as BuilderBobComponentSdk

			if (!componentSdk) {
				logger.captureError(new Error('could not find component SDK'), {
					groupErrorsBy: 'values',
					tags: { method: 'getBuilderBOBCreate$w', compType },
				})
				return undefined
			}

			const getProps = () => modelsApi.getCompProps(compId)
			const setProps = createSetPropsForNCM(compId)

			const $w = shouldUseNewBuilderSdkApi
				? componentSdk.create$w({ getProps, setProps, getService, getSlot })
				: (componentSdk as any)(getProps, setProps, getService, { getSlot })

			builderBOB$wCache[compId] = $w

			return $w
		},
		getComponentSdkFactory(compType) {
			if (componentsSdks[compType]) {
				return componentsSdks[compType]
			}

			const builderComponentSdk = builderComponentsSdks[compType]

			if (!builderComponentSdk) {
				logger.captureError(new Error('Could not find component SDK'), {
					groupErrorsBy: 'values',
					tags: { method: 'getComponentSdkFactory', compType },
				})
				return
			}

			return (sdkFactoryParams: componentSdkFactoryArgs, baseApiFactory?: ComponentSdkFactory | null) => {
				const { getProps, setProps, getService, baseApi } = {
					getProps: () => sdkFactoryParams.props,
					setProps: sdkFactoryParams.NCMSetProps,
					getService: sdkFactoryParams.getService,
					baseApi: baseApiFactory ? baseApiFactory(sdkFactoryParams) : {},
				}
				return shouldUseNewBuilderSdkApi
					? builderComponentSdk(sdkFactoryParams, baseApiFactory)
					: (builderComponentSdk as any)(getProps, setProps, getService, baseApi)
			}
		},
		getSdkTypeToComponentTypes(sdkType: string) {
			return sdkTypesToCompTypesMapper[sdkType] || [sdkType]
		},
	}
}

export default {
	factory: ComponentSdksManager,
	deps: [
		LOAD_COMPONENT_SDKS_PROMISE,
		MODELS_API,
		PLATFORM_LOGGER,
		BOOTSTRAP_DATA,
		BUILDER_COMPONENT_SDK_LOADER,
		SERVICES_LOADER,
		SET_PROPS_MANAGER,
	],
	name: COMPONENT_SDKS_MANAGER,
}
